'use client';

import { useLocale, useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import React from 'react';

import { ActivitiesSection } from '@ui/sections/activities/ActivitiesSection';
import { useCategoryTranslation } from '@website/utils/categoryTranslation';

import type { Activity } from '../../../../../services/activities.service';

interface CategoryActivitiesSectionProps {
  activities: Activity[];
  category: string;
  isLoading?: boolean;
  isError?: boolean;
}

/**
 * Category activities section - displays similar activities in the same category.
 * Fetched client-side (see CategoryActivitiesClient): kept out of the build/SSR
 * so it never blocks/stampedes the catalog query and never serves stale links to
 * activities that have since changed.
 */
export const CategoryActivitiesSection: React.FC<CategoryActivitiesSectionProps> = ({
  activities,
  category,
  isLoading = false,
  isError = false
}) => {
  const router = useRouter();
  const locale = useLocale();
  const t = useTranslations('ActivitiesSection');
  const tActivityDetail = useTranslations('ActivityDetail');
  const tActivityCard = useTranslations('ActivityCard');
  const translateCategory = useCategoryTranslation();

  // Nothing to show: no category, or a settled (not loading) empty result.
  if (!category || (!isLoading && !isError && activities.length === 0)) {
    return null;
  }

  // Get the translated category name
  const translatedCategory = translateCategory(category);

  const getActivityHref = (activityId: string) => `/${locale}/activity/${activityId}`;

  const handleActivityClick = (activityId: string) => {
    router.push(getActivityHref(activityId));
  };

  const handleSeeMoreClick = () => {
    router.push(`/search?category=${encodeURIComponent(category)}`);
  };

  return (
    <ActivitiesSection
      activities={activities}
      isLoading={isLoading}
      isError={isError}
      onActivityClick={handleActivityClick}
      getActivityHref={getActivityHref}
      onSeeMoreClick={handleSeeMoreClick}
      headingText={tActivityDetail('similarInCategory', { category: translatedCategory })}
      seeMoreText={t('seeMore')}
      noActivitiesText={tActivityDetail('noOtherCategoryActivities', {
        category: translatedCategory
      })}
      atHomeText={tActivityCard('atHome')}
      atVenueText={tActivityCard('atVenue')}
      onlineText={tActivityCard('online')}
      bookingsInYearPrefix={tActivityCard('bookingsInYearPrefix')}
      bookingsInYearMiddle={tActivityCard('bookingsInYearMiddle')}
      bookingsInYearSuffix={tActivityCard('bookingsInYearSuffix')}
      onwardsText={tActivityCard('onwards')}
      freeText={tActivityCard('free')}
      fullWidth={true}
      disableVerticalPadding={true}
      showSeeMore={false}
    />
  );
};
